import React from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { makeStyles, MultiSelectAutocomplete, Theme } from '..';

export interface LabeledInputOption {
  key: string;
  label: string;
}

interface FieldSelectorProps {
  fieldName: string;
  label: string;
  limitTags?: number;
  required?: boolean;
  className?: string;
  options: LabeledInputOption[];
  control: Control<FieldValues, object>;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
      // Autocomplete adds padding on hover to make room for clear icon. Fixed paddingRight keeps chips from wrapping on hover
      paddingRight: theme.spacing(7),
      '& .MuiAutocomplete-endAdornment': {
        top: 'auto',
      },
    },
    minHeight: theme.spacing(10.5),
  },
}));

export const FieldSelector = (props: FieldSelectorProps): JSX.Element => {
  const { fieldName, label, limitTags, options, required, control, className } = props;
  const classes = useStyles();

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { value, onChange, ...props }, fieldState: { error } }) => {
        return (
          <MultiSelectAutocomplete
            {...props}
            label={label}
            multiple
            autoComplete
            autoHighlight
            disableCloseOnSelect
            options={options}
            value={value}
            requiredTextfield={required}
            getOptionLabel={(option: LabeledInputOption) => option.label}
            getOptionSelected={(option: LabeledInputOption, value: LabeledInputOption) => option.key === value.key}
            onChange={(_, newValues: LabeledInputOption[]) => onChange(newValues)}
            error={Boolean(error)}
            errorText={error?.message ?? ''}
            renderOption={(option) => option.label}
            className={`${classes.root} ${className}`}
            limitTags={limitTags}
          />
        );
      }}
    />
  );
};
